<script setup>
import { computed } from 'vue';
import WootMessageEditor from 'dashboard/components/widgets/WootWriter/Editor.vue';

const props = defineProps({
  inboxes: { type: Array, default: () => [] },
  // { inbox_id, email_alias_id, subject, message }; an empty array right after the action is picked
  modelValue: { type: [Object, Array], default: () => ({}) },
});

const emit = defineEmits(['update:modelValue']);

// Controlled by the parent: every change emits the whole value, keyed by the inbox id
// (an object would end up in the payload as is).
const value = computed(
  () =>
    (Array.isArray(props.modelValue)
      ? props.modelValue[0]
      : props.modelValue) || {}
);

const update = changes => {
  emit('update:modelValue', {
    inbox_id: null,
    email_alias_id: null,
    subject: '',
    message: '',
    ...value.value,
    ...changes,
  });
};

// The addresses the email can go out from: each inbox's own address, followed by its aliases,
// as in the inbox setting "Respond from email". No alias means the inbox's own address.
const senderOptions = computed(() =>
  props.inboxes.flatMap(inbox => {
    const name = inbox.internal_name || inbox.name;
    return [
      {
        id: String(inbox.id),
        name: inbox.email ? `${name} (${inbox.email})` : name,
        inbox_id: inbox.id,
        email_alias_id: null,
      },
      ...(inbox.email_aliases || []).map(emailAlias => ({
        id: `${inbox.id}:${emailAlias.id}`,
        name: `${name} (${emailAlias.email})`,
        inbox_id: inbox.id,
        email_alias_id: emailAlias.id,
      })),
    ];
  })
);

// Rules created through the API may hold the ids as strings.
const sameId = (a, b) => a != null && b != null && String(a) === String(b);

const selectedSender = computed({
  // An alias deleted after the rule was saved shows the inbox's own address: the rule sends
  // from it, and saving the rule drops the alias (AutomationRule).
  get: () => {
    const { inbox_id: inboxId, email_alias_id: aliasId } = value.value;
    const inboxOptions = senderOptions.value.filter(option =>
      sameId(option.inbox_id, inboxId)
    );
    return (
      inboxOptions.find(option => sameId(option.email_alias_id, aliasId)) ||
      inboxOptions[0] ||
      null
    );
  },
  set: option =>
    update({
      inbox_id: option?.inbox_id ?? null,
      email_alias_id: option?.email_alias_id ?? null,
    }),
});

const subject = computed({
  get: () => value.value.subject || '',
  set: newSubject => update({ subject: newSubject }),
});

const message = computed({
  get: () => value.value.message || '',
  set: newMessage => update({ message: newMessage }),
});
</script>

<template>
  <div class="flex flex-col gap-1 mt-1">
    <div class="multiselect-wrap--small">
      <multiselect
        v-model="selectedSender"
        track-by="id"
        label="name"
        :placeholder="$t('AUTOMATION.ACTION.SENDER_DROPDOWN_PLACEHOLDER')"
        selected-label
        :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
        deselect-label=""
        :max-height="160"
        :options="senderOptions"
        :allow-empty="false"
      />
    </div>
    <input
      v-model="subject"
      type="text"
      class="mb-0"
      :placeholder="$t('AUTOMATION.ACTION.SUBJECT_INPUT_PLACEHOLDER')"
    />
    <WootMessageEditor
      v-model="message"
      rows="4"
      enable-variables
      target-schema="html"
      channel-type="Channel::Email"
      :placeholder="$t('AUTOMATION.ACTION.TEAM_MESSAGE_INPUT_PLACEHOLDER')"
      class="action-message"
    />
  </div>
</template>

<style scoped>
.multiselect {
  margin: var(--space-smaller) var(--space-zero);
}

.action-message {
  @apply mt-1 mx-0 mb-0;
}
</style>
